import { RenderingStatus } from "Enumerations";
import { SearchBoxPopoverModel } from "Components/Nav/AppHeaderMenu/UniversalSearch/SearchBoxPopover.types";
import { Types } from "Shared/Utils";
import { Filter24Filled, FluentIcon, Search20Regular } from "@fluentui/react-icons";
import { AppRegistry } from "Apps/AppRegistry";
import { makeAutoObservable } from "mobx";
import { processSearchResults } from "Utils/universalSearchUtils";

export type universalSearchResultItem = {
    id: string;
    title: string;
    description?: string;
    icon?: FluentIcon;
    onClick?: () => void;
    appPath?: string;
    category?: string;
    originalCategory?: string;
    navigationTarget?: string;
};

export const noResultsItem = {
    id: "no-results",
    title: "No results found",
    description: "",
    category: "No Results",
    icon: undefined,
    onClick: undefined,
    appPath: undefined,
    originalCategory: "No Results",
    navigationTarget: undefined
};

export const categoryIconMap: { [key: string]: FluentIcon } = {
    apps: AppRegistry.AppHub.filledIcon,
    contacts: AppRegistry.Contacts.filledIcon,
    services: AppRegistry.Service.filledIcon,
    teams: AppRegistry.Teams.filledIcon,
    incidents: AppRegistry.Incidents.filledIcon,
    rules: AppRegistry.Rules.filledIcon,
    server: AppRegistry.Server.filledIcon,
    servers: AppRegistry.Server.filledIcon,
    tenants: AppRegistry.CustomerHub.filledIcon,
    servicehub: AppRegistry.Service.filledIcon,
    runtimeinsights: AppRegistry.RuntimeInsights.filledIcon
};

const coreFilters: Set<string> = new Set(["Apps", "Contacts", "Services", "Teams", "Incidents", "More Filters"]);

export const allCategories: Set<string> = new Set([
    "Apps",
    "Contacts",
    "Services",
    "Teams",
    "Incidents",
    "Rules",
    "Servers",
    "Tenants",
    "ServiceHub",
    "RuntimeInsights"
]);

const universalSearch = (id: string, target: string, appPath?: string) => {
    let onSearch = (word: string): Promise<universalSearchResultItem[]> => Promise.resolve([]);
    return {
        id: id,
        target: target,
        appPath: appPath,
        loadingStatus: RenderingStatus.Ready,
        searchResultList: new Array<universalSearchResultItem>(),
        async runSearch(word: string) {
            this.loadingStatus = RenderingStatus.Waiting;
            try {
                this.searchResultList = await onSearch(word);
                this.loadingStatus = RenderingStatus.Ready;
                return this.searchResultList;
            } catch {
                this.loadingStatus = RenderingStatus.Error;
                return [];
            }
        },
        setOnSearch(onsearch: (word: string) => Promise<universalSearchResultItem[]>) {
            onSearch = onsearch;
        }
    };
};

export type universalSearchType = ReturnType<typeof universalSearch>;

export const UniversalSearchStore = () => {
    const store = {
        searchWord: "",
        subscriptionMap: new Map<string, universalSearchType>(),
        currentShownCategories: new Set<string>(["More Filters"]),
        popover: Types.getUndefined<SearchBoxPopoverModel>(),
        placeHolder: "",
        filters: new Array<string>(),
        isGlobalSearch: true,
        hasTable: false,
        contextCategory: Types.getUndefined<string>(),
        setContextCategory(category: string | undefined) {
            this.contextCategory = category;
        },
        subscribe(id: string, target: string, appPath?: string) {
            const item = universalSearch(id, target, appPath);
            this.subscriptionMap.set(id, item);
            if (coreFilters.has(id)) {
                this.currentShownCategories.add(id);
            }
            if (!allCategories.has(id) && !id.endsWith("-widget")) {
                this.hasTable = true;
                this.isGlobalSearch = false;
            }
            return item;
        },
        unSubscribe(id: string) {
            this.subscriptionMap.delete(id);
            this.hasTable = false;
            for (const category of this.subscriptionMap.keys()) {
                if (!allCategories.has(category)) {
                    this.hasTable = true;
                    this.isGlobalSearch = false;
                }
            }
            if (!this.hasTable) {
                this.isGlobalSearch = true;
            }
        },
        async onSearch(word: string) {
            this.searchWord = word;
            let textToSearch = word;

            if (this.filters.length > 0 && this.searchWord.startsWith(`is:${this.filters[0]}`)) {
                textToSearch = word.replace(`is:${this.filters[0]}`, "").trim();
            }

            if (textToSearch === "" || textToSearch === "is:") {
                this.subscriptionMap.forEach((entry) => (entry.searchResultList = new Array<universalSearchResultItem>()));
                return;
            }

            const searchPromises: Promise<universalSearchResultItem[]>[] = [];

            if (this.filters.length > 0) {
                this.subscriptionMap.forEach((entry, key) => {
                    if (this.filters.includes(key)) {
                        searchPromises.push(entry.runSearch(textToSearch));
                    }
                });
            } else if (this.isGlobalSearch) {
                this.subscriptionMap.forEach((entry) => {
                    if (this.currentShownCategories.has(entry.id)) {
                        searchPromises.push(entry.runSearch(textToSearch));
                    }
                });
            } else if (this.hasTable) {
                this.subscriptionMap.forEach((entry) => {
                    if (!allCategories.has(entry.id)) {
                        searchPromises.push(entry.runSearch(textToSearch));
                    }
                });
            }
            await Promise.all(searchPromises);
        },
        setFilter(filter: string | null) {
            this.filters = filter ? [filter] : [];
            this.searchWord = this.searchWord.replace(/^is:[^ ]* /, "");
            this.searchWord = filter ? `is:${filter} ${this.searchWord}` : this.searchWord;
        },
        setGlobalSearch(isGlobal: boolean) {
            this.isGlobalSearch = isGlobal;
        },
        get resultList() {
            if (this.searchWord === "" || this.isGlobalSearch === false) {
                return [];
            }

            if (this.searchWord.startsWith("is:")) {
                const filterText = this.searchWord.substring(3).toLowerCase();
                if (!filterText.includes(" ")) {
                    const matchingCategories = Array.from(this.subscriptionMap.entries())
                        .filter(([key]) => key.toLowerCase().includes(filterText) && allCategories.has(key))
                        .map(([key]) => ({
                            id: key,
                            title: key,
                            description: "",
                            icon: Filter24Filled,
                            onClick: () => {
                                this.setFilter(key);
                                this.searchWord = `is:${key} `;
                                this.currentShownCategories.clear();
                                coreFilters.forEach((filter) => {
                                    this.currentShownCategories.add(filter);
                                });
                                this.currentShownCategories.add(key);
                            },
                            appPath: this.subscriptionMap.get(key)?.appPath,
                            category: "Filters"
                        }));

                    if (matchingCategories.length > 0) {
                        return matchingCategories;
                    }

                    return [noResultsItem];
                }
            }

            const MAX_RESULTS_PER_CATEGORY = 5;
            const keyword = this.searchWord.trim().toLowerCase();
            const effectiveContext = this.searchStatus === RenderingStatus.Ready ? this.contextCategory : undefined;

            const rawResults = Array.from(this.subscriptionMap.entries()).flatMap(([key, { searchResultList }]) =>
                searchResultList.map((item) => {
                    const category = key;
                    if (!item.icon) {
                        const lowerCategory = category.toLowerCase();
                        if (lowerCategory !== "apps") {
                            item.icon = categoryIconMap[lowerCategory] ? categoryIconMap[lowerCategory] : Search20Regular;
                        }
                    }
                    return {
                        ...item,
                        originalCategory: item.category,
                        category,
                        appPath: this.subscriptionMap.get(key)?.appPath
                    };
                })
            );
            let processedResults;
            if (this.filters.length > 0) {
                const filterKeyword = this.searchWord.replace(`is:${this.filters[0]}`, "").trim().toLowerCase();
                const filtered = rawResults.filter((item) => this.filters.includes(item.category));

                processedResults = processSearchResults(filtered, filterKeyword, this.currentShownCategories, MAX_RESULTS_PER_CATEGORY, true, effectiveContext);

                if (processedResults.length === 0 && this.searchWord !== `is:${this.filters[0]}` && this.searchStatus === RenderingStatus.Ready) {
                    processedResults.push(noResultsItem);
                }
                return processedResults;
            }

            processedResults = processSearchResults(rawResults, keyword, this.currentShownCategories, MAX_RESULTS_PER_CATEGORY, false, effectiveContext);

            processedResults.filter((item) => !this.currentShownCategories.has(item.category));

            if (processedResults.length === 0 && this.searchWord !== "" && this.searchStatus === RenderingStatus.Ready) {
                processedResults.push(noResultsItem);
            }
            return processedResults;
        },
        get status() {
            const statusList = new Set(Array.from(this.subscriptionMap.values()).map((value) => value.loadingStatus));
            if (statusList.has(RenderingStatus.Ready)) {
                return RenderingStatus.Ready;
            }
            return RenderingStatus.Waiting;
        },
        get searchStatus() {
            const statusList = new Set(Array.from(this.subscriptionMap.values()).map((value) => value.loadingStatus));
            if (statusList.has(RenderingStatus.Waiting)) {
                return RenderingStatus.Waiting;
            }
            return RenderingStatus.Ready;
        },
        get placeholder() {
            if (!this.isGlobalSearch) {
                const filteredEntries = Array.from(this.subscriptionMap.entries()).filter(([category]) => !allCategories.has(category));

                if (filteredEntries.length > 0) {
                    return `Filter by ${filteredEntries[0]?.[1].target}`;
                }
                return "Search in this page";
            }
            if (this.placeHolder !== "") {
                return this.placeHolder;
            }
            const targetList = Array.from(
                new Set(
                    Array.from(this.subscriptionMap.entries())
                        .filter(([category]) => this.currentShownCategories.has(category))
                        .map(([_, value]) => value.target)
                )
            );
            return "Search " + targetList.join(", ");
        },
        get ids() {
            return Array.from(this.subscriptionMap.keys());
        },
        get currentCategories() {
            const categories = Array.from(this.currentShownCategories);

            const moreFiltersIndex = categories.indexOf("More Filters");

            if (moreFiltersIndex !== -1) {
                const moreFilters = categories.splice(moreFiltersIndex, 1)[0];
                if (moreFilters !== undefined) {
                    categories.push(moreFilters);
                }
            }

            return new Set(categories);
        }
    };

    makeAutoObservable(store);
    return store;
};
